import { Checkbox } from '@rsos/base-components';
import type {
  IarAgency,
  IarMember,
} from '@rsos/reduxDataStore/src/features/eventShare/types';
import styles from './IarAgencySelector.module.scss';
import type { IarSelectedMember } from './helpers';
import {
  buildSelection,
  matchesSearch,
  useResolvedMembers,
} from './iarAgencySelectorHelpers';

interface AgencyMemberListProps {
  org: string;
  agency: IarAgency;
  selectedMembers: IarSelectedMember[];
  onChange: (members: IarSelectedMember[]) => void;
  searchTerm: string;
}

/** Renders the (filtered, alphabetically sorted) member checkboxes for an agency. */
const AgencyMemberList = ({
  org,
  agency,
  selectedMembers,
  onChange,
  searchTerm,
}: AgencyMemberListProps) => {
  const agencyId = agency.legacyId;
  const {
    data: members,
    isLoading,
    isError,
  } = useResolvedMembers(org, agencyId);

  if (isLoading) {
    return <p className={styles.statusMessage}>Loading members...</p>;
  }

  if (isError || !members) {
    return <p className={styles.errorMessage}>Failed to load members.</p>;
  }

  if (members.length === 0) {
    return <p className={styles.statusMessage}>No members found.</p>;
  }

  const isMemberSelected = (memberId: number) =>
    selectedMembers.some(
      m => m.agency_id === agencyId && m.member_id === memberId,
    );

  const handleMemberToggle = (member: IarMember) => {
    if (isMemberSelected(member.legacyId)) {
      onChange(
        selectedMembers.filter(
          m => !(m.agency_id === agencyId && m.member_id === member.legacyId),
        ),
      );
    } else {
      onChange([...selectedMembers, buildSelection(member, agency)]);
    }
  };

  const filtered = searchTerm
    ? members.filter(m => matchesSearch(m, searchTerm))
    : members;

  const sortedMembers = [...filtered].sort((a, b) =>
    `${a.lastName}, ${a.firstName}`.localeCompare(
      `${b.lastName}, ${b.firstName}`,
    ),
  );

  return (
    <>
      {sortedMembers.map(member => (
        <Checkbox
          key={member.legacyId}
          label={`${member.lastName}, ${member.firstName}`}
          description={member.position || ''}
          isChecked={isMemberSelected(member.legacyId)}
          checkboxID={`iar-member-${member.legacyId}`}
          onChange={() => handleMemberToggle(member)}
        />
      ))}
    </>
  );
};

export default AgencyMemberList;
